// THE REPORT PIECES (UI rebuild batch 5 · Profit and Inventory) — what Astra's report mockups share (the four reports'
// tabs are reports-bs/ReportTabs, one row for all four): the period control with its "Read-only" word, the row that opens its own detail in place, the
// panel's foot ("N lines" · "Dubai time"), and the "Basis & supporting detail" fold at the bottom of the page. Read-only
// pieces: nothing here posts, and nothing here is a button that does anything but open or close a line.
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PeriodDates, PeriodSelect } from '../trades/PeriodSelect';
import type { PeriodValue } from '../../PeriodChips';

/** Astra's period control: the word "Period" over the select, "Read-only" beside it; the two Dubai days beneath once
 *  "Dates…" is picked. The choices and the days they mean are the period chips' own (PeriodSelect / periodDays). */
export function PeriodBar({ value, onChange }: { value: PeriodValue; onChange: (v: PeriodValue) => void }) {
  return (
    <>
      <div className="mb-4">
        <div className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Period</div>
        <div className="flex items-center gap-4">
          <div className="flex min-w-0 flex-1 sm:flex-none"><PeriodSelect value={value} onChange={onChange} /></div>
          <span className="shrink-0 text-meta text-tg-hint">Read-only</span>
        </div>
      </div>
      <PeriodDates value={value} onChange={onChange} />
    </>
  );
}

/** A report row that opens its detail in place (Astra: "detail belongs within its accounting line"). `line` is the row
 *  as it reads closed — its own grid on the laptop, its own stack on the phone; `detail` is drawn under it when open. */
export function OpenRow({ line, detail, label }: { line: ReactNode; detail: ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="border-b border-stroke-soft last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${label}: ${open ? 'hide' : 'show'} detail`}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[44px] w-full items-start gap-3 px-4 py-4 text-left hover:bg-panel-2 lg:items-center lg:px-5 lg:py-3"
      >
        <span className="min-w-0 flex-1">{line}</span>
        <Chevron className="mt-0.5 h-4 w-4 shrink-0 text-tg-hint lg:mt-0" aria-hidden />
      </button>
      {open && <div className="border-t border-stroke-soft bg-panel-2/40 px-4 py-2 lg:px-5">{detail}</div>}
    </li>
  );
}

/** The column heads over the rows, from lg (the phone reads each row as its own stack). `grid` is the rows' own grid. */
export function RowHead({ grid, heads }: { grid: string; heads: ReadonlyArray<{ label: string; right?: boolean }> }) {
  return (
    <div className="hidden border-b border-stroke bg-panel-2 px-5 py-3 lg:flex lg:items-center lg:gap-3">
      <div className={`min-w-0 flex-1 ${grid}`}>
        {heads.map((h) => (
          <span key={h.label} className={`text-label font-medium uppercase tracking-wider text-tg-hint ${h.right ? 'text-right' : ''}`}>{h.label}</span>
        ))}
      </div>
      <span className="w-4 shrink-0" aria-hidden />
    </div>
  );
}

/** The panel's foot, as Astra's: the count at the left, "Dubai time" at the right. */
export function PanelFoot({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
      <span className="min-w-0">{children}</span>
      <span>Dubai time</span>
    </div>
  );
}

/** The quiet line under the panel. "Basis & supporting detail" follows it in the screen itself, as a <details> with
 *  these classes — the method said once, behind a tap. */
export function FootLine({ children }: { children: ReactNode }) {
  return <p className="mt-4 border-t border-stroke-soft px-1 py-4 text-meta text-tg-hint lg:px-5">{children}</p>;
}
export const DETAILS = 'border-t border-stroke-soft pb-3 text-meta text-tg-hint';
export const SUMMARY = 'cursor-pointer py-3 text-body text-tg-hint hover:text-tg-text lg:py-2.5';

/** One figure per currency, stacked — a report never adds two currencies together. Absent is a dash, never a zero. */
export function PerCurrency({ items }: { items: ReadonlyArray<{ asset: string; node: ReactNode }> }) {
  if (items.length === 0) return <span className="text-tg-hint">—</span>;
  return (
    <span className="block space-y-0.5">
      {items.map((it) => <span key={it.asset} className="block whitespace-nowrap">{it.node}</span>)}
    </span>
  );
}
